import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyDropdown,
  SkyInput,
  SkyText,
  SkyTooltip,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  PIPELINE_TEMPLATES,
  type ConnectorAdminView,
  type ConnectorSeedDocument,
  type ConnectorUpsertResult,
} from "@shared/types/connectorsAdmin";
import { egressCellText } from "@/lib/egressText";
import { usePinnedColumnEdge } from "@/lib/gridPinned";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { ConnectorDetailDrawer } from "@/components/platform/ConnectorDetailDrawer";
import { ConnectorUsageCard } from "@/components/platform/ConnectorUsageCard";
import { ConnectorPinsCard } from "@/components/platform/ConnectorPinsCard";
import { ConnectorVersionsDialog } from "@/components/platform/ConnectorVersionsDialog";
import {
  EmptyState,
  ErrorState,
  GridSkeleton,
  ScreenNote,
} from "@/components/platform/ScreenState";
import { SeedUploadDialog } from "@/components/platform/SeedUploadDialog";
import {
  useAddConnectorVersion,
  useConnectorsAdmin,
  useRemoveConnectorVersion,
  useSetConnectorActive,
  useSetDefaultVersion,
  useUpsertConnectorSeed,
} from "@/hooks/use-connectors-admin";
import {
  useConnectorAvailability,
  useSetConnectorAvailability,
} from "@/hooks/use-connector-access";
import { reportFailureShown } from "@/lib/feedback/failure";
import { useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { ConnectorsIcon, UploadIcon, ViewIcon } from "@/lib/icons";
import { LIST_GRID_PAGINATION, pixelColumnLayout } from "@/lib/grid";
import { ApiClientError } from "@/lib/api";

/**
 * Connector definitions (FR-30 to FR-33, FR-72, canvas 13).
 *
 * A definition is a ROW: field groups, fields, credential validation, entity
 * selection, images and run tunables. Adding or changing one needs no
 * deployment and no restart, which is the whole point of the screen — the
 * release pipeline imports the image into the registry, and this screen (or the
 * seed script, through the same code path) tells the deployment about it.
 *
 * Nothing here carries a credential: a definition describes HOW to validate
 * one, never a value.
 */

const TEMPLATE_OPTIONS = [
  { value: "all", text: "All templates" },
  ...PIPELINE_TEMPLATES.map((template) => ({ value: template, text: template })),
];

const STATE_OPTIONS = [
  { value: "all", text: "All states" },
  { value: "active", text: "Active" },
  { value: "preview", text: "Preview" },
  { value: "disabled", text: "Disabled" },
];

/** The Egress cell (#992): "1 host" or "N hosts" with the names on hover, "Unrestricted" for none. */
function EgressCell({ name, hosts }: { name: string; hosts: readonly string[] }) {
  const text = egressCellText(hosts.length);
  if (hosts.length === 0) {
    return (
      <SkyText size={200} font="numeric">
        {text}
      </SkyText>
    );
  }
  const names = hosts.join(", ");
  return (
    <SkyTooltip content={names}>
      {/* Focusable so a keyboard user can open the names too. The names sit in `sr-only`, so the
          `aria-describedby` Radix adds while the tooltip is open is dropped (else they read twice). */}
      <span
        className="inline-flex"
        tabIndex={0}
        role="group"
        aria-label={`${text}: ${names}.`}
        aria-describedby={undefined}
        data-testid={`egress-cell-${name}`}
      >
        <SkyText size={200} font="numeric">
          {text}
        </SkyText>
        <span className="sr-only" aria-hidden="true" data-testid={`egress-hosts-${name}`}>
          : {names}.
        </span>
      </span>
    </SkyTooltip>
  );
}

/**
 * #1344: the grid's measured floor, as on Users and Tenants (`pixelColumnLayout`). Each column has
 * a pixel minimum for its header (wrapped at its spaces, sort icon included) and widest cell
 * (Egress "Unrestricted", the Open button), and the table never goes narrower than their sum, which
 * stays inside the 1024 px pane (927) so no value sits under the pinned Actions column there. A
 * narrower pane scrolls sideways with Actions pinned.
 */
const CONNECTORS_GRID_MINIMUMS = {
  displayName: 146,
  version: 142,
  pipelineTemplate: 137,
  state: 84,
  versions: 88,
  cpu: 106,
  egressFqdns: 112,
  actions: 102,
} as const;

/** No definition to list: none matches the filters, or none exists yet (upload one). */
function NoDefinitions({ isFiltered, onUpload }: { isFiltered: boolean; onUpload: () => void }) {
  return (
    <EmptyState
      testId="connectors-empty"
      icon={ConnectorsIcon}
      title={isFiltered ? "No definitions match those filters" : "No connector definitions yet"}
      message={
        isFiltered
          ? "Clear the search or the filters to see the rest."
          : "Run the seed script, or upload a definition file. Until a definition exists, nobody can create a connection."
      }
      action={
        isFiltered ? null : (
          <SkyButton appearance="primary" icon={UploadIcon} onClick={onUpload}>
            Upload seed
          </SkyButton>
        )
      }
    />
  );
}

export default function AdminConnectorsPage() {
  const notice = useNotice();
  // Session 10 (ux-s10 §3): every failure on this screen comes from a control that stays open.
  // The drawer's Activate/Deactivate gets its own bar inside the drawer; the image-tag dialog
  // and the seed dialog already print theirs (`error`), so those are only logged here.
  const toggleError = useInlineError();
  // "Connector created/updated", held until the seed dialog has closed so it is posted outside
  // the modal's hidden window, under the page title.
  const seedConfirmation = useRef<string | null>(null);
  // The same for the drawer and the image-tag dialog it opens (both modal, so the notice region
  // is hidden from a screen reader while either is open): each confirmation is one sentence,
  // held until the last of the two closes, then posted as one notice (review r1 SF1).
  const heldConfirmations = useRef<string[]>([]);
  const modalOpen = useRef(false);
  const { selectedInstance } = useTenantInstance();
  const connectors = useConnectorsAdmin();

  const [search, setSearch] = useState("");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [openName, setOpenName] = useState<string | null>(null);
  // The drawer and the pin card are opened by the same click but closed
  // separately: dismissing the drawer should reveal the pins for the connector
  // just read, not take them away with it.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [versionsName, setVersionsName] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // A refused Deactivate (in use, or usage unknown): the drawer closes and the usage table shows.
  const [deactivateRefusal, setDeactivateRefusal] = useState<{
    name: string;
    error: ApiClientError;
  } | null>(null);

  // The mutations are keyed by the connector they act on, so the hooks take the
  // name of whichever definition the dialogs are currently about.
  const versionsTarget = versionsName ?? "";
  const addVersion = useAddConnectorVersion(versionsTarget);
  const removeVersion = useRemoveConnectorVersion(versionsTarget);
  const setDefaultVersion = useSetDefaultVersion(versionsTarget);
  const setActive = useSetConnectorActive();
  // T-51: "Not available" switches a connector off for new connections in every tenant.
  const availability = useConnectorAvailability();
  const setAvailability = useSetConnectorAvailability();
  const notAvailable = new Set(availability.data?.notAvailable ?? []);
  const upsertSeed = useUpsertConnectorSeed();

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (connectors.data ?? []).filter((connector) => {
      if (templateFilter !== "all" && connector.pipelineTemplate !== templateFilter) return false;
      if (stateFilter === "active" && !connector.isActive) return false;
      if (stateFilter === "disabled" && connector.isActive) return false;
      if (stateFilter === "preview" && !connector.isPreview) return false;
      if (term === "") return true;
      return (
        connector.name.toLowerCase().includes(term) ||
        connector.displayName.toLowerCase().includes(term) ||
        (connector.imageName ?? "").toLowerCase().includes(term)
      );
    });
  }, [connectors.data, search, templateFilter, stateFilter]);

  // Actions stay at the right edge when the grid scrolls sideways (#1344, as Tenants). The grid
  // mounts only after the load (and again after a filter that matched nothing), and the hook
  // attaches once when enabled, so it is enabled only while the grid is on screen.
  const pinnedEdge = usePinnedColumnEdge<HTMLDivElement>(
    !connectors.isPending && !connectors.isError && rows.length > 0,
  );

  const openConnector = useMemo(
    () => (connectors.data ?? []).find((connector) => connector.name === openName) ?? null,
    [connectors.data, openName],
  );

  const versionsConnector = useMemo(
    () => (connectors.data ?? []).find((connector) => connector.name === versionsName) ?? null,
    [connectors.data, versionsName],
  );

  useEffect(() => {
    modalOpen.current = drawerOpen || versionsName !== null;
  }, [drawerOpen, versionsName]);

  /** Post now when nothing modal is open (a response that lands after the close), else hold. */
  function confirmAfterClose(sentence: string): void {
    if (!modalOpen.current) {
      notice.success(sentence);
      return;
    }
    heldConfirmations.current.push(sentence);
  }

  function postHeldConfirmations(): void {
    const sentences = heldConfirmations.current;
    if (sentences.length === 0) return;
    heldConfirmations.current = [];
    notice.success(sentences.join(" "));
  }

  async function classifySeed(seed: ConnectorSeedDocument): Promise<ConnectorUpsertResult> {
    return upsertSeed.mutateAsync({ seed, dryRun: true, force: false });
  }

  async function applySeed(
    seed: ConnectorSeedDocument,
    force: boolean,
  ): Promise<ConnectorUpsertResult> {
    const result = await upsertSeed.mutateAsync({ seed, dryRun: false, force });
    seedConfirmation.current =
      result.action === "insert" ? "Connector created" : "Connector updated";
    return result;
  }

  const columns: SkyDataGridColumn<ConnectorAdminView>[] = [
    {
      key: "displayName",
      header: "Connector",
      sortable: true,
      width: "10%",
      renderCell: (_value, row) => (
        <div className="flex flex-col">
          <SkyText size={300} weight="medium" block>
            {row.displayName}
          </SkyText>
          <SkyText size={200} color="muted" block>
            {row.name}
            {row.imageName ? ` · ${row.imageName}` : ""}
          </SkyText>
        </div>
      ),
    },
    {
      key: "version",
      // Round 9 (B-50): this is the seed definition's own version. The image a run starts is the
      // default tag in the Image tags column, a separate pin; the two differ by design.
      header: "Definition",
      sortable: true,
      width: "10%",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {row.version}
        </SkyText>
      ),
    },
    {
      key: "pipelineTemplate",
      header: "Template",
      sortable: true,
      width: "10%",
      renderCell: (_value, row) => (
        <SkyText size={200} font="monospace">
          {row.pipelineTemplate}
        </SkyText>
      ),
    },
    {
      key: "state",
      header: "State",
      width: "10%",
      renderCell: (_value, row) => (
        <div className="flex flex-wrap items-center gap-1">
          <SkyBadge appearance="tint" color={row.isActive ? "success" : "subtle"}>
            {row.isActive ? "Active" : "Disabled"}
          </SkyBadge>
          {row.isPreview ? (
            <SkyBadge appearance="outline" color="warning">
              Preview
            </SkyBadge>
          ) : null}
          {notAvailable.has(row.name) ? (
            <SkyBadge appearance="outline" color="subtle">
              Not available
            </SkyBadge>
          ) : null}
        </div>
      ),
    },
    {
      key: "versions",
      header: "Image tags",
      width: "10%",
      renderCell: (_value, row) => (
        <div className="flex flex-wrap gap-1">
          {row.versions.length === 0 ? (
            <SkyText size={200} color="muted">
              none
            </SkyText>
          ) : (
            row.versions.map((version) => (
              <SkyBadge
                key={version.tag}
                appearance={version.isDefault ? "tint" : "outline"}
                color={version.isDefault ? "brand" : "subtle"}
              >
                {version.isDefault ? `${version.tag} · default` : version.tag}
              </SkyBadge>
            ))
          )}
        </div>
      ),
    },
    {
      key: "cpu",
      header: "CPU / memory",
      width: "10%",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {row.cpu} / {row.memoryGb} GB
        </SkyText>
      ),
    },
    {
      key: "egressFqdns",
      header: "Egress",
      align: "right",
      width: "10%",
      renderCell: (_value, row) => <EgressCell name={row.name} hosts={row.egressFqdns} />,
    },
    {
      key: "actions",
      header: "Actions",
      width: "10%",
      renderCell: (_value, row) => (
        <SkyButton
          appearance="subtle"
          size="small"
          icon={ViewIcon}
          onClick={() => {
            setOpenName(row.name);
            setDrawerOpen(true);
          }}
          aria-label={`Open ${row.displayName}`}
        >
          Open
        </SkyButton>
      ),
    },
  ];

  const layout = pixelColumnLayout(columns, {
    minimums: CONNECTORS_GRID_MINIMUMS,
    selection: false,
  });
  const isFiltered = search.trim() !== "" || templateFilter !== "all" || stateFilter !== "all";

  function renderConnectors(): ReactNode {
    if (connectors.isPending) {
      return (
        <GridSkeleton rows={5} label="Loading connector definitions…" testId="connectors-loading" />
      );
    }
    if (connectors.isError) {
      return (
        <ErrorState
          title="The connector list did not load"
          error={connectors.error}
          onRetry={() => void connectors.refetch()}
          testId="connectors-error"
        />
      );
    }
    if (rows.length === 0) {
      return <NoDefinitions isFiltered={isFiltered} onUpload={() => setUploadOpen(true)} />;
    }
    return (
      <>
        <div
          ref={pinnedEdge}
          style={{ "--connectors-grid-floor": `${layout.floor}px` } as CSSProperties}
        >
          <SkyDataGrid<ConnectorAdminView>
            data={rows}
            columns={layout.columns}
            {...LIST_GRID_PAGINATION}
            getRowId={(row) => row.name}
            className="grid-pinned-last [&_table]:min-w-(--connectors-grid-floor) [&_th]:whitespace-normal"
            testId="connectors-grid"
          />
        </div>
        <SkyText size={200} color="muted" block>
          {rows.length === (connectors.data ?? []).length
            ? `${rows.length} definitions.`
            : `${rows.length} of ${(connectors.data ?? []).length} definitions match the filters.`}{" "}
          A definition is a row in the platform database: adding or changing one needs no
          deployment.
        </SkyText>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="connectors-page">
      <TopbarActionsPortal>
        <SkyButton
          appearance="primary"
          icon={UploadIcon}
          onClick={() => {
            upsertSeed.reset();
            setUploadOpen(true);
          }}
          testId="upload-seed"
        >
          Upload seed
        </SkyButton>
      </TopbarActionsPortal>

      <ScreenNote
        title="Definitions are data, not code"
        message="A new connector or version is added through this screen or the seed script. The release pipeline imports the image into the registry. The app needs no deployment and no restart."
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <SkyInput
            label="Search connectors"
            type="search"
            value={search}
            clearable
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch("")}
            testId="connector-search"
          />
        </div>
        <div className="w-52">
          <SkyDropdown
            label="Template"
            options={TEMPLATE_OPTIONS}
            selectedOptions={[templateFilter]}
            onOptionSelect={(values) => setTemplateFilter(values[0] ?? "all")}
            testId="connector-template-filter"
          />
        </div>
        <div className="w-52">
          <SkyDropdown
            label="State"
            options={STATE_OPTIONS}
            selectedOptions={[stateFilter]}
            onOptionSelect={(values) => setStateFilter(values[0] ?? "all")}
            testId="connector-state-filter"
          />
        </div>
      </div>

      {renderConnectors()}

      {openConnector && !drawerOpen && deactivateRefusal?.name === openConnector.name ? (
        <ConnectorUsageCard
          key={`usage-${openConnector.name}-${deactivateRefusal ? "refused" : "open"}`}
          connector={openConnector}
          refusal={deactivateRefusal.error}
        />
      ) : null}
      {openConnector && !drawerOpen ? <ConnectorPinsCard connector={openConnector} /> : null}

      <ConnectorDetailDrawer
        open={drawerOpen && openConnector !== null}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (open) return;
          toggleError.clear();
          modalOpen.current = versionsName !== null;
          if (!modalOpen.current) postHeldConfirmations();
        }}
        connector={openConnector}
        hasInstanceContext={selectedInstance !== null}
        onOpenVersions={() => {
          if (openConnector) setVersionsName(openConnector.name);
        }}
        isTogglingActive={setActive.isPending}
        availableForNew={openConnector ? !notAvailable.has(openConnector.name) : true}
        isTogglingAvailable={setAvailability.isPending}
        onToggleAvailable={() => {
          if (!openConnector) return;
          toggleError.clear();
          const available = notAvailable.has(openConnector.name);
          setAvailability.mutate(
            { name: openConnector.name, available },
            {
              onSuccess: () =>
                confirmAfterClose(
                  available
                    ? `${openConnector.displayName} is available for new connections.`
                    : `${openConnector.displayName} is not available for new connections. Existing connections keep working.`,
                ),
              onError: (error) => toggleError.show(error, "That change was not saved."),
            },
          );
        }}
        toggleError={toggleError.error}
        onToggleActive={() => {
          if (!openConnector) return;
          toggleError.clear();
          setDeactivateRefusal(null);
          setActive.mutate(
            { name: openConnector.name, isActive: !openConnector.isActive },
            {
              onSuccess: () =>
                confirmAfterClose(
                  openConnector.isActive ? "Connector deactivated." : "Connector activated.",
                ),
              onError: (error) => {
                if (
                  error instanceof ApiClientError &&
                  (error.code === "CONNECTOR_IN_USE" || error.code === "CONNECTOR_USAGE_UNKNOWN")
                ) {
                  // The drawer closes onto the usage table, which says what to delete first.
                  setDeactivateRefusal({ name: openConnector.name, error });
                  setDrawerOpen(false);
                  return;
                }
                // Inside the drawer, which stays open with the button that failed.
                toggleError.show(error, "That change was not saved.");
              },
            },
          );
        }}
      />

      <ConnectorVersionsDialog
        key={`versions-${versionsName ?? "none"}`}
        open={versionsConnector !== null}
        onOpenChange={(open) => {
          if (open) return;
          setVersionsName(null);
          modalOpen.current = drawerOpen;
          if (!modalOpen.current) postHeldConfirmations();
        }}
        connector={versionsConnector}
        isSaving={addVersion.isPending || removeVersion.isPending || setDefaultVersion.isPending}
        error={addVersion.error ?? removeVersion.error ?? setDefaultVersion.error}
        onAddVersion={(values) =>
          addVersion.mutate(
            { ...values, isDefault: false },
            {
              onSuccess: () => confirmAfterClose(`${values.tag} added.`),
              onError: (error) => reportFailureShown(error),
            },
          )
        }
        onRemoveVersion={(tag) =>
          removeVersion.mutate(tag, {
            onSuccess: () => confirmAfterClose(`${tag} removed.`),
            onError: (error) => reportFailureShown(error),
          })
        }
        onSetDefault={(tag) =>
          setDefaultVersion.mutate(
            { tag },
            {
              onSuccess: () => confirmAfterClose(`${tag} is the default.`),
              onError: (error) => reportFailureShown(error),
            },
          )
        }
      />

      <SeedUploadDialog
        key={`seed-${uploadOpen}`}
        open={uploadOpen}
        onOpenChange={(open) => {
          setUploadOpen(open);
          const confirmation = seedConfirmation.current;
          if (!open && confirmation) {
            seedConfirmation.current = null;
            notice.success(confirmation);
          }
        }}
        connectors={connectors.data ?? []}
        onClassify={classifySeed}
        onApply={applySeed}
        isClassifying={upsertSeed.isPending}
        isApplying={upsertSeed.isPending}
        error={upsertSeed.error}
      />
    </div>
  );
}
